import { Box, Text, Title } from '@mantine/core'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { clsx } from '@mantine/styles'

import { useAuth } from '@percent/workplace-giving/common/hooks'
import {
  getAccountFromAuthState,
  getPartnerFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import classes from './WelcomeHeader.module.scss'

type WelcomeHeaderProps = Readonly<{
  lightMode: boolean
}>

const interpolateGreeting = (greeting: string, userName: string): string => {
  return greeting.replace('{{preferredName}}', userName)
}

export function WelcomeHeader({ lightMode }: WelcomeHeaderProps) {
  const { t } = useTranslation()
  const authState = useAuth()
  const account = getAccountFromAuthState(authState.state)!
  const partner = getPartnerFromAuthState(authState.state)!
  const { homePageBannerContent } = partner

  const userName = useMemo(
    (): string => account?.preferredName || account?.fullName || '',
    [account?.fullName, account?.preferredName]
  )

  const getGreetingTitle = useMemo(() => {
    if (homePageBannerContent?.welcomeCopyTitle) {
      return interpolateGreeting(homePageBannerContent.welcomeCopyTitle, userName)
    }

    return t('workplace_giving.new_home.greeting.title.regular', {
      name: account?.preferredName || account?.fullName || undefined,
      interpolation: { escapeValue: false }
    })
  }, [account?.fullName, account?.preferredName, homePageBannerContent, t, userName])

  const colorStyle =
    homePageBannerContent?.welcomeCopyColor &&
    typeof CSS !== 'undefined' &&
    CSS.supports('color', homePageBannerContent.welcomeCopyColor)
      ? { color: homePageBannerContent.welcomeCopyColor }
      : undefined

  return (
    <Box className={clsx(classes.GreetingContainer, { [classes.GreetingContainerLightMode]: lightMode })}>
      <Title order={2} className={classes.GreetingTitle} style={colorStyle}>
        {getGreetingTitle}
      </Title>
      <Text className={classes.GreetingSubtitle} style={colorStyle}>
        {homePageBannerContent?.welcomeCopySubtitle
          ? interpolateGreeting(homePageBannerContent.welcomeCopySubtitle, userName)
          : t('workplace_giving.new_home.greeting.subtitle')}
      </Text>
    </Box>
  )
}
